<script lang="ts">
  import type { Dataset } from '../types.ts';
  import { STATUS_LABEL, type ConditionTrack } from '../tracking/collate.ts';
  import { fmtDate, fmtMonth, fmtNum, fmtDelta, statusTone } from '../format.ts';
  import Sparkline from '../charts/Sparkline.svelte';

  let { ds, tracks, onOpen }: { ds: Dataset; tracks: ConditionTrack[]; onOpen: (id: string) => void } = $props();
  const attention = $derived(tracks.filter((t) => t.status === 'needs-attention'));
  const band = (t: ConditionTrack) => { const tg = t.primary?.spec.target; if (!tg) return null; return tg.kind === 'range' ? { low: tg.low, high: tg.high } : tg.kind === 'below' || tg.kind === 'atOrBelow' ? { high: tg.value } : { low: tg.value }; };
  const trendGlyph = (t: string) => (t === 'improving' ? '↘ improving' : t === 'worsening' ? '↗ worsening' : t === 'stable' ? '→ stable' : '');
  const toneColor = (t: ConditionTrack) => `var(--${statusTone[t.status] === 'muted' ? 'text-3' : statusTone[t.status]})`;
  const first = $derived(ds.visits[0]);
  const last = $derived(ds.visits[ds.visits.length - 1]);
</script>

<div class="ov">
  <section class="hero">
    <div class="herofig">
      <span class="lbl">HbA1c today</span>
      <span class="big num">{fmtNum(last.glycemic.hba1c, 1)}<small>%</small></span>
      <span class="t-foot">from {fmtNum(first.glycemic.hba1c, 1)}% at diagnosis · {fmtDate(last.date)}</span>
    </div>
    <dl class="facts">
      <div><dt>Age / sex</dt><dd>{ds.patient.ageYears} · {ds.patient.sex}</dd></div>
      <div><dt>Diagnosed</dt><dd>{fmtMonth(ds.patient.diagnosis.date)} (age {ds.patient.diagnosis.ageAtDiagnosis})</dd></div>
      <div><dt>Weight</dt><dd>{fmtNum(last.vitals.weightKg, 1)} kg <span class="faint">(peak {fmtNum(ds.summary.peakWeightKg, 1)})</span></dd></div>
      <div><dt>Blood pressure</dt><dd class="warnish">{ds.summary.bp} <span class="faint">(was {ds.summary.bpFourYearsAgo} in 2022)</span></dd></div>
      <div><dt>LDL / TG</dt><dd>{ds.summary.ldl} / {ds.summary.tg} <span class="faint">(from {ds.summary.ldlAtDiagnosis} / {ds.summary.tgAtDiagnosis})</span></dd></div>
      <div><dt>eGFR / UACR</dt><dd>{ds.summary.egfr} / {ds.summary.uacr ?? '—'} <span class="faint">(peak UACR {ds.summary.peakUacr})</span></dd></div>
      <div><dt>Allergies</dt><dd>{ds.patient.allergies.join(', ')}</dd></div>
      <div><dt>Visits on record</dt><dd>{ds.visits.length} over {Math.round((Date.parse(last.date) - Date.parse(first.date)) / (365.25 * 86400000))} years</dd></div>
    </dl>
  </section>

  {#if attention.length}
    <section class="attn" aria-label="Needs attention">
      <h2>Needs attention</h2>
      <ul>
        {#each attention as t (t.spec.id)}
          <li><button class="link" onclick={() => onOpen(t.spec.id)}>{t.spec.name}</button><span>{t.statusReason}</span></li>
        {/each}
        {#each ds.summary.attention.filter((a) => a.severity === 'low') as a (a.item)}
          <li class="low"><span class="itm">{a.item}</span><span>{a.detail}</span></li>
        {/each}
      </ul>
    </section>
  {/if}

  <section>
    <h2>Conditions tracked</h2>
    <div class="cards">
      {#each tracks as t (t.spec.id)}
        <button class="card" onclick={() => onOpen(t.spec.id)}>
          <div class="cardhead">
            <span class="cname">{t.spec.shortName}</span>
            <span class="pill {statusTone[t.status]}">{STATUS_LABEL[t.status]}</span>
          </div>
          {#if t.primary && t.primary.latest}
            <div class="val">
              <span class="num big2">{fmtNum(t.primary.latest.value, t.primary.spec.decimals)}</span>
              <span class="unit">{t.primary.spec.unit}</span>
              {#if t.primary.delta != null}<span class="delta num" title="change since previous visit">{fmtDelta(t.primary.delta, t.primary.spec.decimals)}</span>{/if}
            </div>
            <div class="row2">
              <Sparkline values={t.primary.points.slice(-12).map((p) => p.value)} color={toneColor(t)} band={band(t)} />
              <div class="mini t-foot">
                <span>{t.primary.spec.label}{#if t.primary.spec.target} · target {t.primary.spec.target.label}{/if}</span>
                <span>{trendGlyph(t.primary.trend)}{#if t.primary.atGoalPct12m != null} · {t.primary.atGoalPct12m}% of last 12 mo at goal{/if}</span>
              </div>
            </div>
          {:else}
            <div class="t-foot">{t.careHistory[0]?.detail ?? t.spec.description}</div>
          {/if}
          {#if t.untreated}<div class="untreated">No treatment on the medication list</div>{/if}
        </button>
      {/each}
    </div>
  </section>

  <section>
    <h2>Treatment eras</h2>
    <div class="eras">
      {#each ds.summary.eras as e, i (e.era)}
        <div class="era">
          <span class="eranum">{i + 1}</span>
          <div><div class="eralbl">{e.label}</div><div class="t-foot">{fmtMonth(e.start)} → {e.end ? fmtMonth(e.end) : 'now'}</div><div class="eradet">{e.detail}</div></div>
        </div>
      {/each}
    </div>
  </section>

  <section class="two">
    <div>
      <h2>Current medications</h2>
      <ul class="meds">
        {#each ds.summary.currentMedications as m (m.name)}
          <li><b>{m.name}</b> <span>{m.dose}</span><span class="t-foot">{m.class} · {m.route} · since {fmtMonth(m.since)}</span></li>
        {/each}
      </ul>
    </div>
    <div>
      <h2>Background</h2>
      <ul class="bg">
        {#each ds.patient.familyHistory as f (f)}<li>{f}</li>{/each}
        <li>{ds.patient.social.smoking}; alcohol {ds.patient.social.alcohol}</li>
        <li>{ds.patient.social.activity}</li>
        <li>{ds.patient.occupation}; {ds.patient.ethnicity}; height {ds.patient.heightCm} cm</li>
      </ul>
    </div>
  </section>
</div>

<style>
  .ov { display: flex; flex-direction: column; gap: var(--space-6); }
  h2 { margin-bottom: var(--space-3); }
  .hero { display: grid; grid-template-columns: auto 1fr; gap: var(--space-8); align-items: center; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-lg); padding: var(--space-6) var(--space-8); }
  @media (max-width: 800px) { .hero { grid-template-columns: 1fr; gap: var(--space-4); } }
  .herofig { display: flex; flex-direction: column; gap: 2px; }
  .lbl { font-size: var(--fs-foot); font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text-3); }
  .big { font-size: 52px; font-weight: 600; line-height: 1; letter-spacing: -0.02em; }
  .big small { font-size: 22px; color: var(--text-3); margin-left: 2px; }
  .facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: var(--space-3) var(--space-5); }
  .facts dt { font-size: var(--fs-foot); color: var(--text-3); }
  .facts dd { font-weight: 600; }
  .faint { color: var(--text-3); font-weight: 400; }
  .warnish { color: var(--danger); }

  .attn { background: var(--danger-soft); border: 1px solid color-mix(in srgb, var(--danger) 30%, transparent); border-radius: var(--radius-lg); padding: var(--space-4) var(--space-5); }
  .attn h2 { color: var(--danger); }
  .attn ul { list-style: none; display: flex; flex-direction: column; gap: var(--space-2); }
  .attn li { display: grid; grid-template-columns: 220px 1fr; gap: var(--space-3); }
  .attn li.low { color: var(--text-2); }
  .itm { font-weight: 700; }
  .link { text-align: left; font-weight: 700; color: var(--danger); text-decoration: underline; text-underline-offset: 3px; }
  @media (max-width: 700px) { .attn li { grid-template-columns: 1fr; } }

  .cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: var(--space-3); }
  .card { text-align: left; display: flex; flex-direction: column; gap: var(--space-2); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-4); transition: border-color var(--ease), box-shadow var(--ease); }
  .card:hover { border-color: var(--accent); box-shadow: 0 2px 12px rgba(22, 86, 196, 0.08); }
  .cardhead { display: flex; justify-content: space-between; align-items: center; gap: var(--space-2); }
  .cname { font-weight: 700; }
  .pill { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; border-radius: var(--radius-pill); padding: 2px 8px; white-space: nowrap; }
  .pill.danger { color: var(--danger); background: var(--danger-soft); }
  .pill.warn { color: var(--warn); background: var(--warn-soft); }
  .pill.accent { color: var(--accent); background: var(--accent-soft); }
  .pill.ok { color: var(--ok); background: var(--ok-soft); }
  .pill.muted { color: var(--text-3); background: var(--surface-2); }
  .val { display: flex; align-items: baseline; gap: 6px; }
  .big2 { font-size: 26px; font-weight: 600; line-height: 1; }
  .unit { color: var(--text-3); }
  .delta { margin-left: auto; color: var(--text-2); font-size: var(--fs-foot); }
  .row2 { display: flex; gap: var(--space-3); align-items: center; }
  .mini { display: flex; flex-direction: column; min-width: 0; }
  .untreated { font-size: var(--fs-foot); color: var(--danger); font-weight: 700; }

  .eras { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--space-3); }
  .era { display: flex; gap: var(--space-3); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-4); }
  .eranum { width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; background: var(--accent-soft); color: var(--accent); font-weight: 800; flex: 0 0 auto; }
  .eralbl { font-weight: 700; }
  .eradet { color: var(--text-2); margin-top: 4px; }

  .two { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-6); }
  @media (max-width: 800px) { .two { grid-template-columns: 1fr; } }
  .meds, .bg { list-style: none; display: flex; flex-direction: column; gap: var(--space-2); }
  .meds li { display: flex; flex-direction: column; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: var(--space-2) var(--space-3); }
  .meds li span:not(.t-foot) { color: var(--text-2); }
  .bg li { color: var(--text-2); padding-left: 14px; position: relative; }
  .bg li::before { content: '•'; position: absolute; left: 0; color: var(--text-3); }
</style>
